import { Decoration } from "@tiptap/pm/view";

import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { afterDelayedFocus } from "@/utils/afterDelayedFocus";
import { normalizeMathText } from "@/utils/normalizeMathText";

import type { LatexMarker, LatexPair } from "./types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeJson } from "@corca/data";
import type { ChainedCommands, Editor } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

let latexMathParser: ((str: string) => NodeJson) | null = null;

// TODO: asyncronous initialization
// https://linear.app/corca-research/issue/D-2675/asyncronous-initialization

export function getLatexMathParser(): ((str: string) => NodeJson) | null {
  return latexMathParser;
}

/**
 * Check if a node type should be excluded from LaTeX autoconversion.
 * Returns true for code blocks, headings, document titles, and inline expressions.
 */
export function shouldExcludeNodeFromLatexConversion(node: ProseMirrorNode): boolean {
  return !node.isTextblock || ["codeBlock", "heading", "documentTitle"].includes(node.type.name);
}

import(/* webpackChunkName: "latex" */ "@corca/latex").then(({ parseMathLatex }) => {
  latexMathParser = parseMathLatex;
});

/**
 * Find all "$" and "$$" markers in text nodes within a block
 * Returns markers sorted by position
 */
export function findLatexMarkersInTextNodes(
  blockNode: ProseMirrorNode,
  blockStart: number,
): LatexMarker[] {
  const markers: LatexMarker[] = [];

  // Iterate through child nodes
  blockNode.forEach((child, offset) => {
    // Only process text nodes
    if (!child.isText) return;

    const nodeStart = blockStart + offset;
    const textContent = child.text || "";
    let searchIndex = 0;

    while (searchIndex < textContent.length) {
      // Look for both "$" and "$$"
      const singleIndex = textContent.indexOf("$", searchIndex);

      if (singleIndex === -1) break;

      // Check if it's "$$" or just "$"
      if (singleIndex + 1 < textContent.length && textContent[singleIndex + 1] === "$") {
        markers.push({
          pos: nodeStart + singleIndex,
          type: "$$",
        });
        searchIndex = singleIndex + 2;
      } else {
        markers.push({
          pos: nodeStart + singleIndex,
          type: "$",
        });
        searchIndex = singleIndex + 1;
      }
    }
  });

  return markers;
}

/**
 * Extract text content between two positions from a document
 */
function extractTextContent(doc: ProseMirrorNode, from: number, to: number): string {
  let text = "";
  doc.nodesBetween(from, to, (node, pos) => {
    if (node.isText) {
      const start = Math.max(from, pos);
      const end = Math.min(to, pos + node.nodeSize);
      const offset = start - pos;
      const length = end - start;
      text += (node.text || "").substring(offset, offset + length);
    }
  });
  return text;
}

/**
 * Parse markers into pairs.
 * "$" markers look for matching "$", ignoring "$$" in between
 * "$$" markers look for matching "$$", ignoring "$" in between
 * If no pair found, content extends to cursor position (if between start and end of block) or end of block
 */
export function findLatexPairs(
  markers: LatexMarker[],
  doc: ProseMirrorNode,
  parentEnd: number,
  cursorPos: number,
): LatexPair[] {
  const pairs: LatexPair[] = [];
  const used = new Set<number>();

  for (let i = 0; i < markers.length; i++) {
    if (used.has(i)) continue;

    const startMarker = markers[i];
    let endMarker: LatexMarker | null = null;

    // Search for matching end marker of the same type
    for (let j = i + 1; j < markers.length; j++) {
      if (used.has(j)) continue;

      const candidate = markers[j];

      // Match same type: "$" with "$", "$$" with "$$"
      if (candidate.type === startMarker.type) {
        endMarker = candidate;
        used.add(j);
        break;
      }
    }

    used.add(i);

    let content: string;
    const contentStart = startMarker.pos + startMarker.type.length;

    if (endMarker) {
      // Complete pair: extract text between markers
      content = extractTextContent(doc, contentStart, endMarker.pos);
    } else if (cursorPos >= contentStart && cursorPos <= parentEnd) {
      // Unclosed: content goes to cursor position if cursor is between start and end of block
      content = extractTextContent(doc, contentStart, cursorPos);
    } else {
      // Otherwise, use end of text block
      content = extractTextContent(doc, contentStart, parentEnd);
    }

    pairs.push({
      start: startMarker,
      end: endMarker,
      content,
    });
  }

  return pairs;
}

/**
 * Insert an inline expression as display mode (in its own centered paragraph)
 * Handles smart paragraph splitting based on surrounding content
 */
export function insertInlineExpressionAsDisplayMode(
  prevChain: ChainedCommands,
  inlineNode: InlineNodeModel,
  from: number,
  to: number,
  docModel: DocumentModel,
  focusTarget: "before" | "after" | null = null,
) {
  const { tiptap } = docModel.editor;
  if (!tiptap) return prevChain;

  let chain = prevChain;

  const { state } = tiptap;
  const $from = state.doc.resolve(from);
  const $to = state.doc.resolve(to);

  // Check if positions are in the same text block
  if ($from.parent === $to.parent && $from.parent.isTextblock) {
    const blockStart = $from.start();
    const textBefore = $from.parent.textContent.substring(0, from - blockStart);
    const textAfter = $from.parent.textContent.substring(to - blockStart);

    const hasContentBefore = textBefore.trim().length > 0;
    const hasContentAfter = textAfter.trim().length > 0;

    if (focusTarget) {
      chain = chain.focus(from);
    }

    let paragraphPos = from;

    if (!hasContentBefore && !hasContentAfter) {
      // No content before or after: just replace with expression
      chain = chain.deleteRange({ from, to }).insertInlineExpression(inlineNode, from);
    } else if (!hasContentBefore) {
      // No content before: insert expression, then split after
      chain = chain
        .deleteRange({ from, to })
        .insertInlineExpression(inlineNode, from)
        .command(({ tr }) => {
          // Split after the expression
          tr.split(from + 1);
          return true;
        });
    } else if (!hasContentAfter) {
      // No content after: split before, then insert expression
      chain = chain
        .deleteRange({ from, to })
        .insertInlineExpression(inlineNode, from)
        .command(({ tr }) => {
          // Split at the position where markers were
          tr.split(from);
          return true;
        });
      paragraphPos += 2;
    } else {
      // Content on both sides: create new paragraph for expression
      chain = chain
        .deleteRange({ from, to })
        .appendParagraph({ pos: from })
        .insertInlineExpression(inlineNode, from + 2);
      paragraphPos += 2;
    }

    chain = chain.command(({ tr }) => {
      tr.setNodeAttribute(paragraphPos - 1, "alignment", "center");
      return true;
    });

    if (focusTarget) {
      afterDelayedFocus(() => {
        docModel.cursor.moveTo(
          inlineNode,
          focusTarget === "before" ? 0 : (inlineNode.nodes.length ?? 0),
        );
      });
    }
  } else {
    // Fallback for cross-block case (shouldn't happen in normal use)
    console.warn(
      "Expected LaTeX markers to be within the same text block. Inserting expression in a new paragraph.",
    );
    chain = chain.deleteRange({ from, to });
    if (focusTarget) {
      chain = chain.focus(from);
    }
    chain = chain.appendParagraph({ pos: from }).insertInlineExpression(inlineNode, from + 2);

    if (focusTarget) {
      afterDelayedFocus(() => {
        docModel.cursor.moveTo(
          inlineNode,
          focusTarget === "before" ? 0 : (inlineNode.nodes.length ?? 0),
        );
      });
    }
  }

  return chain;
}

function restoreSelection(tiptap: Editor, callback: (chain: ChainedCommands) => ChainedCommands) {
  const { state } = tiptap;
  const prevSelection = state.selection;
  let chain = tiptap.chain();

  chain = callback(chain);

  return chain.command(({ tr }) => {
    const newSelection = prevSelection.map(tr.doc, tr.mapping);
    tr.setSelection(newSelection);
    return true;
  });
}

export function convertLatexToExpression(
  latexContent: string,
  startMarker: LatexMarker,
  endMarker: LatexMarker,
  docModel: DocumentModel,
  focusTarget: "before" | "after" | null = null,
) {
  const parseMathLatex = getLatexMathParser();
  if (!parseMathLatex) return;

  const normalizedContent = normalizeMathText(latexContent);
  const nodeJson: NodeJson = parseMathLatex(normalizedContent);

  if (docModel && nodeJson) {
    const inlineNode = docModel.transact(
      () => nodeFromJson(docModel, nodeJson),
      "Tiptap:parseLatex",
    );

    const { tiptap } = docModel.editor;
    if (tiptap && inlineNode?.is(InlineNodeModel)) {
      queueMicrotask(() => {
        const from = startMarker.pos;
        const to = endMarker.pos + endMarker.type.length;

        restoreSelection(tiptap, (_chain) => {
          let chain = _chain;
          // For display mode ($$), handle paragraph splitting
          if (startMarker.type === "$$") {
            return insertInlineExpressionAsDisplayMode(
              chain,
              inlineNode,
              from,
              to,
              docModel,
              focusTarget,
            );
          }

          // Inline mode ($): just replace and insert
          chain.deleteRange({ from, to });

          if (focusTarget) {
            chain = chain.focus(from);
          }

          chain = chain.insertInlineExpression(inlineNode, from);

          if (focusTarget) {
            afterDelayedFocus(() => {
              docModel.cursor.moveTo(
                inlineNode,
                focusTarget === "before" ? 0 : (inlineNode.nodes.length ?? 0),
              );
            });
          }

          return chain;
        }).run();
      });
    }
  }
}

export function createLatexDecorations({
  pair,
  isCursorInside,
  hasError,
  activeSuggestions,
}: {
  pair: LatexPair;
  isCursorInside: boolean;
  hasError?: boolean;
  activeSuggestions?: boolean;
}): Decoration[] {
  const decorations: Decoration[] = [];
  const { start, end, content } = pair;

  // Gray out opening marker ("$" or "$$")
  decorations.push(
    Decoration.inline(start.pos, start.pos + start.type.length, {
      class: "text-gray-500",
    }),
  );

  if (end) {
    // Complete pair: gray out closing marker
    decorations.push(
      Decoration.inline(end.pos, end.pos + end.type.length, {
        class: "text-gray-500",
      }),
    );

    // Blue/red underline for content (only when cursor is inside or there's an error)
    if (content.length > 0 && (isCursorInside || hasError)) {
      decorations.push(
        Decoration.inline(start.pos + start.type.length, end.pos, {
          class: hasError ? "underline decoration-red-500" : "underline decoration-blue-500",
          spellcheck: "false",
        }),
      );
    }
  } else if (content.length > 0) {
    if (isCursorInside && activeSuggestions) {
      // Unclosed pair: blue underline for content
      const contentStart = start.pos + start.type.length;
      const contentEnd = contentStart + content.length;
      decorations.push(
        Decoration.inline(contentStart, contentEnd, {
          class: "underline decoration-blue-500",
          spellcheck: "false",
        }),
      );
    }
  }

  return decorations;
}
